import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      /**
       * Re-read the ref instead of using the one captured above.
       *
       * 2026-09-03: this threw
       *   InvalidStateError: Failed to execute 'showModal' on
       *   'HTMLDialogElement': The element is not in a Document
       * on add-to-cart, and the cart drawer silently never opened - the
       * reported symptom being "the button spins and then nothing happens".
       *
       * showDialog() captures `dialog` synchronously but calls showModal() a
       * frame later. Add-to-cart re-renders the cart-drawer section in
       * between, and the section morph REPLACES the dialog element - so the
       * captured reference is detached by the time the frame runs, and
       * showModal() on a detached element throws.
       *
       * It reproduced for one account and not another purely on timing: a
       * slower page (or a heavier drawer - a saved-fit block adds markup)
       * lets the morph land inside the one-frame gap that a fast page clears
       * first. That is a race, not a per-customer feature difference, so it
       * can surface for anyone under the right load.
       */
      let target = this.refs.dialog ?? dialog;

      /**
       * RECOVER, don't just bail.
       *
       * First revision of this fix returned early when the element was
       * detached. That stopped the InvalidStateError and the stuck scroll
       * lock, but the drawer still silently never opened - which is the
       * actual reported symptom, so it fixed the crash and not the bug.
       *
       * Two distinct detach cases, both real here because add-to-cart morphs
       * the cart-drawer section while this is in flight:
       *   a) `this` is still in the document but its dialog child was
       *      replaced -> re-query inside ourselves.
       *   b) `this` component was itself replaced by the morph -> the live
       *      replacement is a different instance of the same custom element,
       *      already connected and carrying the fresh markup. Hand off to it.
       *      (`document.querySelector` only ever returns connected elements,
       *      and the identity check stops it recursing into itself.)
       */
      if (!target?.isConnected) {
        if (this.isConnected) {
          target = this.querySelector('dialog') ?? null;
        } else {
          const live = document.querySelector(this.localName);
          if (live && live !== this && typeof live.showDialog === 'function') {
            live.showDialog();
          }
          return;
        }
      }

      // Bail BEFORE touching body styles. The original applied the scroll
      // lock first and then threw, stranding the page fixed-positioned with
      // no dialog open and nothing left to unlock it.
      if (!target?.isConnected || target.open) return;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      try {
        target.showModal();
      } catch (error) {
        // Unwind the scroll lock rather than leaving the page stuck.
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        console.warn('[dialog] showModal failed; drawer not opened', error);
        return;
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
